const MAX_DEPTH = 8;
const MAX_ITEMS_NUM = 50;

export default function buildOctree(indices, positions, bbox, isPointCloud) {

  const stack = [0];
  const levels = [0];
  const leaves = [];

  // minx, maxx, miny, maxy, minz, maxz
  const bboxArray = [];
  // offset, length
  const sizeArray = [];
  // parent index
  const linkArray = [];

  const oneThird = 1 / 3;

  let currIndex = 0;

  sizeArray.push(0, indices.length);
  linkArray.push(-1);
  bboxArray.push.apply(bboxArray, bbox);

  let buildStartTime;

  if (typeof performance !== "undefined") {
    buildStartTime = performance.now();
  }

  while (stack.length) {

    const cellInd = stack.pop();
    const cellDepth = levels.pop();

    const cellLength = sizeArray[cellInd * 2 + 1];
    const itemsNum = cellLength * oneThird;

    if (itemsNum > MAX_ITEMS_NUM && cellDepth < MAX_DEPTH) {

      addChildren(cellInd, indices, positions, bboxArray, sizeArray, isPointCloud);

      stack.push(currIndex + 1, currIndex + 2, currIndex + 3, currIndex + 4,
                 currIndex + 5, currIndex + 6, currIndex + 7, currIndex + 8);

      const nextLevelDepth = cellDepth + 1;
      levels.push(nextLevelDepth, nextLevelDepth, nextLevelDepth, nextLevelDepth,
                  nextLevelDepth, nextLevelDepth, nextLevelDepth, nextLevelDepth);

      linkArray.push(cellInd, cellInd, cellInd, cellInd,
                     cellInd, cellInd, cellInd, cellInd);

      currIndex += 8;

    } else {

      leaves.push(cellInd);

    }

  }

  for (let i = 0; i < leaves.length; ++i) {
    const leafInd = leaves[i];
    const leafLength = sizeArray[leafInd * 2 + 1];
    if (leafLength) {
      createLeaf(leafInd, indices, positions, bboxArray, sizeArray, linkArray);
    }
  }

  const bboxBuffer = new Float32Array(bboxArray);
  const sizeBuffer = new Uint32Array(sizeArray);

  const messages = [];
  if (typeof performance !== "undefined") {
    messages.push("Octree: building time: " +
                      ((performance.now() - buildStartTime) / 1000).toPrecision(3) + " sec.");
  }
  return {indices, positions, bboxBuffer, sizeBuffer, messages};
}

function addChildren(parentInd, indices, positions, bboxArray, sizeArray, isPointCloud) {

  const parentBBOfset = parentInd * 6;

  const pminx = bboxArray[parentBBOfset];
  const pminy = bboxArray[parentBBOfset + 1];
  const pminz = bboxArray[parentBBOfset + 2];

  const pmaxx = bboxArray[parentBBOfset + 3];
  const pmaxy = bboxArray[parentBBOfset + 4];
  const pmaxz = bboxArray[parentBBOfset + 5];

  const cenX = 0.5 * (pminx + pmaxx);
  const cenY = 0.5 * (pminy + pmaxy);
  const cenZ = 0.5 * (pminz + pmaxz);

  const halfX = 0.25 * (pmaxx - pminx);
  const halfY = 0.25 * (pmaxy - pminy);
  const halfZ = 0.25 * (pmaxz - pminz);

  const mx = cenX - halfX;
  const px = cenX + halfX;

  const my = cenY - halfY;
  const py = cenY + halfY;

  const mz = cenZ - halfZ;
  const pz = cenZ + halfZ;

  bboxArray.push(mx - halfX, my - halfY, mz - halfZ, mx + halfX, my + halfY, mz + halfZ);
  bboxArray.push(mx - halfX, my - halfY, pz - halfZ, mx + halfX, my + halfY, pz + halfZ);
  bboxArray.push(px - halfX, my - halfY, mz - halfZ, px + halfX, my + halfY, mz + halfZ);
  bboxArray.push(px - halfX, my - halfY, pz - halfZ, px + halfX, my + halfY, pz + halfZ);

  bboxArray.push(mx - halfX, py - halfY, mz - halfZ, mx + halfX, py + halfY, mz + halfZ);
  bboxArray.push(mx - halfX, py - halfY, pz - halfZ, mx + halfX, py + halfY, pz + halfZ);
  bboxArray.push(px - halfX, py - halfY, mz - halfZ, px + halfX, py + halfY, mz + halfZ);
  bboxArray.push(px - halfX, py - halfY, pz - halfZ, px + halfX, py + halfY, pz + halfZ);

  const childrenIndicesList = [];

  for (let i = 0; i < 8; ++i) {
    childrenIndicesList.push([]);
  }

  const indices0 = childrenIndicesList[0];
  const indices1 = childrenIndicesList[1];
  const indices2 = childrenIndicesList[2];
  const indices3 = childrenIndicesList[3];
  const indices4 = childrenIndicesList[4];
  const indices5 = childrenIndicesList[5];
  const indices6 = childrenIndicesList[6];
  const indices7 = childrenIndicesList[7];

  const oneThird = 1 / 3;

  const startInd = sizeArray[parentInd * 2];
  const finalInd = startInd + sizeArray[parentInd * 2 + 1];

  let x, y, z;

  const primitive = isPointCloud ? new Array(1) : new Array(3);
  const stride = isPointCloud ? 1 : 3;
  for (let i = startInd; i < finalInd; i += stride) {

    if (isPointCloud) {

      const v = indices[i];
      const offset = v * 3;

      x = positions[offset];
      y = positions[offset + 1];
      z = positions[offset + 2];

      primitive[0] = v;

    } else {

      const v1 = indices[i];
      const v2 = indices[i + 1];
      const v3 = indices[i + 2];

      const offset1 = v1 * 3;
      const offset2 = v2 * 3;
      const offset3 = v3 * 3;

      x = (positions[offset1]     + positions[offset2]     + positions[offset3]) * oneThird;
      y = (positions[offset1 + 1] + positions[offset2 + 1] + positions[offset3 + 1]) * oneThird;
      z = (positions[offset1 + 2] + positions[offset2 + 2] + positions[offset3 + 2]) * oneThird;

      primitive[0] = v1;
      primitive[1] = v2;
      primitive[2] = v3;

    }

    if (y < cenY) {
      if (x < cenX) {
        if (z < cenZ) {indices0.push.apply(indices0, primitive); }
        else { indices1.push.apply(indices1, primitive); }
      } else { // x
        if (z < cenZ) { indices2.push.apply(indices2, primitive); } // z
        else { indices3.push.apply(indices3, primitive); } // z
      } // x
    } else { // y
      if (x < cenX) {
        if (z < cenZ) { indices4.push.apply(indices4, primitive); } // z
        else { indices5.push.apply(indices5, primitive); } // z
      } else { // x
        if (z < cenZ) { indices6.push.apply(indices6, primitive); } // z
        else { indices7.push.apply(indices7, primitive); } // z
      } // x
    } // y

  }

  let offset = sizeArray[parentInd * 2];

  for (let i = 0; i < childrenIndicesList.length; ++i) {

    const cellIndices = childrenIndicesList[i];

    indices.set(cellIndices, offset);

    sizeArray.push(offset, cellIndices.length);

    offset += cellIndices.length;

  }

}

function createLeaf(leafInd, indices, positions, bboxArray, sizeArray, linkArray) {

  let maxx = -Infinity;
  let minx = Infinity;
  let maxy = -Infinity;
  let miny = Infinity;
  let maxz = -Infinity;
  let minz = Infinity;

  const startInd = sizeArray[leafInd * 2];
  const finalInd = startInd + sizeArray[leafInd * 2 + 1];

  for (let i = startInd; i < finalInd; i++) {

    const offset = indices[i] * 3;

    const x = positions[offset];
    const y = positions[offset + 1];
    const z = positions[offset + 2];

    maxx = Math.max(maxx, x);
    minx = Math.min(minx, x);
    maxy = Math.max(maxy, y);
    miny = Math.min(miny, y);
    maxz = Math.max(maxz, z);
    minz = Math.min(minz, z);

  }

  expandAABBLoose(leafInd, linkArray, bboxArray, minx, maxx, miny, maxy, minz, maxz);

}

function expandAABBLoose(leafInd, linkArray, bboxArray, minx, maxx, miny, maxy, minz, maxz) {

  let parentInd = leafInd;

  while (parentInd > 0) {

    const offset = parentInd * 6;
    let updated = false;

    if (bboxArray[offset] > minx) {

      bboxArray[offset] = minx;
      updated = true;

    }

    if (bboxArray[offset + 1] > miny) {

      bboxArray[offset + 1] = miny;
      updated = true;

    }

    if (bboxArray[offset + 2] > minz) {

      bboxArray[offset + 2] = minz;
      updated = true;

    }

    if (bboxArray[offset + 3] < maxx) {

      bboxArray[offset + 3] = maxx;
      updated = true;

    }

    if (bboxArray[offset + 4] < maxy) {

      bboxArray[offset + 4] = maxy;
      updated = true;

    }

    if (bboxArray[offset + 5] < maxz) {

      bboxArray[offset + 5] = maxz;
      updated = true;

    }

    parentInd = updated ? linkArray[parentInd] : -1;

  }

}
